import './CommentDisplay.scss';

import { CommentAuthor } from './CommentAuthor';
import { Comment as CommentBase } from '@ant-design/compatible';
import { Editor } from '@ui/Editor';
import { SanitizedHtml } from '@ui/general';
import { SonarUser } from '@ui/SonarUser';
import { TaggedUser } from '@sonar-software/sonar-ts-types';

import { coreActions, InitiativeComment } from '@sonar-software/sonar-core';
import React, { useCallback } from 'react';
import { useAction, useToggle } from '@hooks';

export interface InitiativeCommentProps {
	comment: InitiativeComment;
}

export const CommentDisplay: React.FC<InitiativeCommentProps> = ({ comment }) => {
	const [ isEditing, toggleEditing ] = useToggle(false);

	const remove = useAction(coreActions.requestDeleteCoreInitiativesCommentsById);
	const update = useAction(coreActions.requestPatchCoreInitiativesCommentsById);

	const { addedBy, addedOn, body, bodyHtml, id, initiativeId } = comment;

	const handleDelete = useCallback(
		() =>
			remove({
				id,
				initiativeId
			}),
		[ id, initiativeId, remove ]
	);

	const handleOnSaveClick = useCallback(
		(updatedBody: string, updatedBodyHtml: string, taggedUsers: Array<TaggedUser>) => {
			update({
				body: updatedBody,
				bodyHtml: updatedBodyHtml,
				id: comment.id,
				initiativeId: comment.initiativeId,
				taggedUsers
			});

			toggleEditing();
		},
		[ comment.id, comment.initiativeId, toggleEditing, update ]
	);

	const displayContent = isEditing ?
		<Editor
			bordered
			content={ body }
			editing
			enableMentions
			onCancel={ toggleEditing }
			onSave={ handleOnSaveClick } />	 :
		<SanitizedHtml html={ bodyHtml } />;

	return (
		<CommentBase
			author={
				<CommentAuthor
					authorUserId={ addedBy }
					commentId={ id }
					createDate={ addedOn }
					onDelete={ handleDelete }
					onEdit={ toggleEditing } />
			}
			avatar={ <SonarUser
				userId={ addedBy }
				withFullName={ false } /> }
			className='initiative-comment'
			content={ displayContent } />
	);
};
